﻿import { zodResolver } from "@hookform/resolvers/zod";
import { Link , useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useSignupMutation } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";

const schema = z.object({
  email: z.string().email("Geçerli bir email adresi gir."),
  password: z.string().min(8, "Şifre en az 8 karakter olmalı."),
  companyName: z.string().min(2, "Firma adı en az 2 karakter olmalı.").max(200),
  phoneNumber: z
    .string()
    .regex(/^\+[1-9]\d{7,14}$/, "Telefon E.164 formatında olmalı (örn: +905551234567)."),
});

type SignupFormValues = z.infer<typeof schema>;

export function SignupPage() {
  const navigate = useNavigate();
  const signup = useSignupMutation();

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
      companyName: "",
      phoneNumber: "+90",
    },
  });

  async function onSubmit(values: SignupFormValues) {
    try {
      const result = await signup.mutateAsync(values);
      toast.success(
        `Hesabın oluşturuldu! Email adresine gönderdiğimiz onay linkini kontrol et. Shop URL'in: ${result.defaultShopSlug}`,
      );
      navigate(`/check-email?email=${encodeURIComponent(values.email)}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "email_taken") {
          form.setError("email", { message: err.detail });
          return;
        }
        toast.error(err.detail);
      } else {
        toast.error("Beklenmeyen bir hata oluştu, tekrar dene.");
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/40">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">TicketNjoy'a hoş geldin</CardTitle>
          <CardDescription>
            Etkinliğini satmaya başlamak için hesap oluştur. 7.5% komisyon, başka masraf yok.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Firma / Marka Adı</FormLabel>
                    <FormControl>
                      <Input placeholder="Örn: Mehmet Prodüksiyon" {...field} />
                    </FormControl>
                    <FormDescription>
                      İlk mağazan bu isimle açılır, sonra değiştirebilirsin.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="sen@ornek.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefon</FormLabel>
                    <FormControl>
                      <Input placeholder="+905551234567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Şifre</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="En az 8 karakter" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={signup.isPending}
              >
                {signup.isPending ? "Hesap oluşturuluyor..." : "Hesap Oluştur"}
              </Button>
            </form>
          </Form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Hesabın var mı?{" "}
            <Link to="/login" className="font-medium text-foreground hover:underline">
              Giriş yap
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
